import React from 'react';
import { Link } from 'react-router-dom';

const AboutUs = () => {
    return (
        <div className="max-w-4xl mx-auto py-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 sm:p-8 lg:p-12">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6">About Us</h1>

                <div className="prose prose-gray max-w-none">
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Who We Are</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            RewriteKing is a cutting-edge text rewriting platform designed to help you communicate with confidence
                            and professionalism. We understand that in today's digital-first world, the way you communicate matters
                            more than ever—whether you're sending a quick email, crafting a business message, or preparing important
                            correspondence.
                        </p>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Our mission is simple: to make professional communication accessible to everyone, regardless of their
                            writing background or expertise. We believe that everyone deserves the tools to express themselves
                            clearly and professionally.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">The Problem We Solve</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Have you ever found yourself staring at a message, wondering if it sounds professional enough? Or
                            perhaps you've sent a casual text that needed to be more formal? We've all been there. The challenge
                            of adapting your communication style to different contexts can be time-consuming and stressful.
                        </p>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            RewriteKing solves this problem by instantly transforming your casual messages into polished,
                            professional communications. With just a few clicks, you can adapt your message to any tone or context
                            you need.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">How It Works</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            RewriteKing uses advanced AI technology to understand your message's intent and rewrite it according to
                            your chosen tone. Simply paste your text, select the desired tone, and get instant results. Our
                            service offers five distinct tone options to match any communication need:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                                <i className="fas fa-briefcase text-primary-600 dark:text-primary-400 mt-1"></i>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Professional</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Clear, direct, and business-appropriate communication</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                                <i className="fas fa-gavel text-primary-600 dark:text-primary-400 mt-1"></i>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Formal</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Structured, respectful, and traditional language</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                                <i className="fas fa-handshake text-primary-600 dark:text-primary-400 mt-1"></i>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Friendly</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Warm, approachable, yet professional tone</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                                <i className="fas fa-compress-alt text-primary-600 dark:text-primary-400 mt-1"></i>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Concise</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Brief, clear, and to the point messaging</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                                <i className="fas fa-heart text-primary-600 dark:text-primary-400 mt-1"></i>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">Empathetic</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Understanding, supportive, and considerate communication</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Why Choose RewriteKing?</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                            <div className="p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <div className="flex items-center gap-3 mb-2">
                                    <i className="fas fa-shield-alt text-primary-600 dark:text-primary-400"></i>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Privacy First</h3>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    We don't collect, store, or retain any of your personal information or text content. Your privacy is our top priority.
                                </p>
                            </div>
                            <div className="p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <div className="flex items-center gap-3 mb-2">
                                    <i className="fas fa-bolt text-primary-600 dark:text-primary-400"></i>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Instant Results</h3>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Get professionally rewritten text in seconds. No waiting, no delays—just fast, reliable results.
                                </p>
                            </div>
                            <div className="p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <div className="flex items-center gap-3 mb-2">
                                    <i className="fas fa-mobile-alt text-primary-600 dark:text-primary-400"></i>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Fully Responsive</h3>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Works seamlessly on desktop, tablet, and mobile devices. Access RewriteKing anywhere, anytime.
                                </p>
                            </div>
                            <div className="p-5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <div className="flex items-center gap-3 mb-2">
                                    <i className="fas fa-dollar-sign text-primary-600 dark:text-primary-400"></i>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Free to Use</h3>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    No sign-up required, no hidden fees, no credit card needed. Start rewriting your text right away.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Our Commitment to You</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            At RewriteKing, we're committed to providing you with the best possible experience:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                            <li><strong className="text-gray-900 dark:text-gray-100">Quality:</strong> We continuously improve our AI models to deliver accurate, contextually appropriate rewrites</li>
                            <li><strong className="text-gray-900 dark:text-gray-100">Privacy:</strong> Your text is processed in real-time and never stored—your content remains yours alone</li>
                            <li><strong className="text-gray-900 dark:text-gray-100">Accessibility:</strong> Our intuitive interface makes professional rewriting accessible to everyone</li>
                            <li><strong className="text-gray-900 dark:text-gray-100">Innovation:</strong> We're constantly working to enhance our service with new features and improvements</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Get Started Today</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Ready to transform your communication? Start using RewriteKing now—it's free, fast, and requires no
                            registration. Simply paste your text, choose your tone, and get professional results in seconds.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">We'd Love to Hear From You</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Have questions, feedback, or suggestions? We're always looking to improve and would appreciate your
                            input. Feel free to reach out to us at{' '}
                            <a href="mailto:contact@rewriteking.com" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                                contact@rewriteking.com
                            </a>
                            {' '}and we'll get back to you within 24 hours.
                        </p>
                    </section>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
                    <Link to="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AboutUs;

